/**
 * Push Recovery — reaches EXISTING generated apps via the CDN platform.js (OS-V7HMWA / #3795)
 *
 * WHY THIS LIVES IN THE RUNTIME SDK
 * ---------------------------------
 * PR #3792 added a React `PushHealthBanner` to the generated-app TEMPLATE. Template
 * files are copied by `App#copy_template_files`, an **after_create** hook — so they
 * land ONLY at app creation. Every app that already existed when #3792 merged (Susan's
 * live app, OS-V7HMWA, is the reference case) has ZERO trace of that banner and cannot
 * receive it without a regeneration + redeploy.
 *
 * `platform.js` is different: it is a CDN asset that EVERY existing app already loads
 * (`https://sdk.overskill.com/v1/platform.js`), and it is hot-updatable — flipping the
 * CDN's `V1_LATEST` pointer reaches every app on its next page load with no per-app
 * redeploy. So this module puts the dead-push recovery surface where it can actually
 * reach the affected users.
 *
 * WHAT IT DOES
 * ------------
 * On init (never on a user's behalf), and only when the browser supports Web Push and
 * the user appears authenticated, it GETs the app's OWN `/api/push/status` route — the
 * generated worker already serves this (`handlePushStatus`) and returns the relay's
 * derived `health` block verbatim, scoped to the calling user (`pushSelfIdentity`). It
 * consumes ONLY `health.needs_resubscribe === true` — the whole point of #3792 was that
 * callers stop inferring deadness from subscription counts or failure codes. When true,
 * it renders one unmissable-but-non-destructive banner (Shadow DOM, no global CSS) whose
 * single action re-subscribes the browser.
 *
 * INVARIANTS (see #3795 "Browser-verified finding")
 * --------------------------------------------------
 *  - Shows ONLY on `health.needs_resubscribe === true`. Never on `never_subscribed`,
 *    never on a transient 5xx / network error, never for an unauthenticated visitor,
 *    never when Web Push is unsupported.
 *  - Dismissible PER SESSION only (sessionStorage). localStorage would permanently
 *    silence it and recreate the original OS-V7HMWA bug.
 *  - Never throws into the host app: every fetch / DOM / permission error is caught.
 *    Push is non-essential and must never degrade the host app. The ONE thing the user
 *    sees on failure is a one-line reason after THEIR click on "Turn on" (SW-250 — a
 *    silently resetting button read as broken).
 *  - Never shows in a frame or on a preview host (SW-250): the permission prompt cannot
 *    appear there, so the button could only ever fail.
 *  - Does NOT auto-request notification permission on load. Permission is requested only
 *    from the user's explicit click on the re-enable button.
 *  - Coordinates with the template's React banner so EXACTLY ONE banner ever shows — see
 *    `reactBannerPresent()`.
 */

import { NATIVE_REGISTER_KEY, resolveRootPushRegistration } from './push-sw-scope';
import type { PlatformConfig, PushRecoveryInstance } from './types';

/** Same-origin route the generated worker already serves (returns the relay `health` block). */
const STATUS_PATH = '/api/push/status';

/** Where overskill-sdk's auth-client persists the app-user JWT (auth-client.ts:26/216). */
const TOKEN_STORAGE_KEY = 'overskill_token';

/**
 * Cross-origin logout marker cookie (issue #2169). overskill-sdk's `logout()` drops
 * a `.overskill.app`-scoped `overskill_logout_<appId>` cookie (value = `Date.now()`
 * in ms) so every sibling subdomain OF THAT APP invalidates its own per-origin
 * `overskill_token` on next SDK init. This constant is the legacy unscoped name and
 * the base of the per-app name; the legacy cookie itself is never read (it signed
 * the user out of every app). Kept in lockstep with
 * overskill-sdk/src/auth-session.ts `LOGOUT_COOKIE_NAME`.
 */
const LOGOUT_COOKIE_NAME = 'overskill_logout';

/**
 * SHARED with the template's PushHealthBanner (PushHealthBanner.tsx:32). Using the SAME
 * key means a dismissal on either surface suppresses both for the session — important
 * for the narrow #3792-era window where an unmarked React banner and this one could both
 * be present.
 */
const DISMISS_KEY = 'overskill_push_reenable_dismissed';

/**
 * Attribute stamped on both banners for DOM-level coordination:
 *  - the template banner carries `="react"` (added in this PR),
 *  - this SDK banner carries `="sdk"`.
 */
const BANNER_ATTR = 'data-overskill-push-recovery';

/**
 * Synchronous window flag the template banner sets at module-eval time (before this
 * module's async status fetch can resolve). Its presence means the app ships the React
 * banner and OWNS the push-recovery surface — the SDK defers entirely. Race-free for
 * every app generated from this PR forward.
 */
const REACT_BANNER_FLAG = '__overskillReactPushBanner';

/**
 * Default service-worker path the template ships and overskill-sdk registers.
 *
 * ⚠️ Registering this at scope "/" REPLACES whatever registration owns "/" —
 * including the Workbox precache worker, which is the #3705 collision. Only
 * reached when `resolveRootPushRegistration()` found no push-capable incumbent.
 */
const SW_PATH = '/sw-push.js';

/** Copy intentionally matches the template banner verbatim. */
const BANNER_COPY = 'Notifications stopped working on this device — tap to re-enable.';

/**
 * Host suffixes of the editor's live preview (E2B raw edge + our proxy). A preview is
 * never where a creator's customers receive push, and the editor embeds it in a
 * cross-origin frame where browsers refuse the permission prompt (SW-250).
 */
const PREVIEW_HOST_SUFFIXES = ['.overskillprojects.dev', '.e2b.app'];

/**
 * The canonical published-preview host (`Deployment::PreviewUrlBuilder`):
 * `preview-<id>.overskill.app` in production, `staging-preview-<id>…` on staging.
 * A subscription there would bind the PREVIEW origin, not the live app.
 */
const CANONICAL_PREVIEW_HOST = /^(?:staging-)?preview-[a-z0-9-]+\.overskill\.app$/;

/**
 * True when "Turn on" cannot work here: the page is framed (the editor preview, or any
 * embed — browsers block `Notification.requestPermission()` from a cross-origin frame)
 * or it is served from a preview host. SW-250: a creator tapped the banner inside the
 * editor preview, the prompt was silently blocked, and the button read as broken.
 * Any doubt (cross-origin `window.top` access throwing) counts as framed.
 */
export function pushRecoveryUnavailableHere(
  win: { self?: unknown; top?: unknown; location?: { hostname?: string } } = window
): boolean {
  try {
    if (win.self !== win.top) return true;
  } catch {
    return true;
  }
  try {
    const host = String(win.location?.hostname || '').toLowerCase();
    return CANONICAL_PREVIEW_HOST.test(host) || PREVIEW_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
  } catch {
    return false;
  }
}

/**
 * The one-line reason shown after a failed re-enable, so the button never silently
 * snaps back to "Turn on" (SW-250). Copy is shared verbatim with the template banner.
 */
export function pushReenableFailureReason(input: {
  permission?: string | null;
  isIOS?: boolean;
  standalone?: boolean;
}): string {
  if (input.permission === 'denied') {
    return 'Notifications are blocked for this app. Allow them in your browser or phone settings, then tap Turn on again.';
  }
  if (input.isIOS && !input.standalone) {
    return 'On iPhone, open this app from your Home Screen icon, then tap Turn on.';
  }
  return "Couldn't turn notifications on. Please try again.";
}

function detectIOS(): boolean {
  try {
    const ua = navigator.userAgent || '';
    return (
      /iP(hone|ad|od)/.test(ua) ||
      (navigator.platform === 'MacIntel' && (navigator as unknown as { maxTouchPoints?: number }).maxTouchPoints! > 1)
    );
  } catch {
    return false;
  }
}

function detectStandalone(): boolean {
  try {
    return (
      (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
      (navigator as unknown as { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}

function registerRootPushWorker(): Promise<ServiceWorkerRegistration> {
  const container = navigator.serviceWorker as ServiceWorkerContainer & Record<string, unknown>;
  const native = container[NATIVE_REGISTER_KEY];
  if (typeof native === 'function') {
    return (native as ServiceWorkerContainer['register'])(SW_PATH, { scope: '/' });
  }
  return navigator.serviceWorker.register(SW_PATH, { scope: '/' });
}

function currentPermission(): string | null {
  try {
    return typeof Notification !== 'undefined' ? String(Notification.permission) : null;
  } catch {
    return null;
  }
}

export class PushRecovery implements PushRecoveryInstance {
  private enabled = false;

  /** Permission answer seen during the last re-enable attempt (drives the failure reason). */
  private lastPermission: string | null = null;

  /**
   * Resolves once the (fire-and-forget) init routine has fully settled. Not awaited in
   * production — init runs to completion on its own — but tests await it to assert the
   * post-fetch outcome deterministically.
   */
  readonly ready: Promise<void>;

  constructor(private config: PlatformConfig) {
    if (this.shouldEnable()) {
      this.enabled = true;
      this.ready = this.run().catch(() => {
        // Push is non-essential — any unexpected failure resolves quietly.
      });
    } else {
      this.ready = Promise.resolve();
    }
  }

  /** Synchronous, cheap gate. The deep gating (support/auth/health) happens in `run()`. */
  private shouldEnable(): boolean {
    if (typeof window === 'undefined' || typeof document === 'undefined') return false;
    // Opt-out via config (data-push-recovery="false" or APP_CONFIG.PUSH_RECOVERY_ENABLED=false).
    if (this.config.pushRecovery === false) return false;
    return true;
  }

  /**
   * The full evaluation pipeline. Each early-return is an invariant. Wrapped by the
   * constructor's `.catch` so nothing here can ever throw into the host app.
   */
  private async run(): Promise<void> {
    // (a) The browser must actually support Web Push, else a re-enable prompt is pointless.
    if (!this.isPushSupported()) {
      this.debugLog('Web Push unsupported — skipping');
      return;
    }

    // (a2) Never in a frame or on a preview host — the permission prompt cannot
    // appear there, so "Turn on" could only ever fail (SW-250).
    if (pushRecoveryUnavailableHere()) {
      this.debugLog('Framed or preview host — skipping');
      return;
    }

    // (4) Defer if the app already ships the template's React banner (new apps have both).
    if (this.reactBannerPresent()) {
      this.debugLog('React PushHealthBanner present — deferring');
      return;
    }

    // (2) Respect a per-session dismissal (shared key with the template banner).
    if (this.isDismissed()) {
      this.debugLog('Dismissed this session — skipping');
      return;
    }

    // (b) Only proceed for an apparently-authenticated user.
    const token = this.getToken();
    if (!token) {
      this.debugLog('No auth token — skipping');
      return;
    }

    // (c/d) Same-origin status read; consume ONLY health.needs_resubscribe === true.
    const needsResubscribe = await this.fetchNeedsResubscribe(token);
    if (!needsResubscribe) {
      this.debugLog('needs_resubscribe not true — skipping');
      return;
    }

    // Re-check the deferral signals: the React banner may have mounted, or the user may
    // have dismissed, during the status round-trip.
    if (this.reactBannerPresent() || this.isDismissed()) {
      this.debugLog('Deferral signal appeared during fetch — skipping');
      return;
    }

    this.renderBanner();
  }

  // ---------------------------------------------------------------------------
  // Gates
  // ---------------------------------------------------------------------------

  /** Matches overskill-sdk PushClient.isSupported() exactly. */
  private isPushSupported(): boolean {
    try {
      return (
        typeof navigator !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window
      );
    } catch {
      return false;
    }
  }

  /**
   * True if the app already presents a template PushHealthBanner, so the SDK must NOT
   * add a second one. Three signals, in order of reliability:
   *  1. `window.__overskillReactPushBanner` — set synchronously by this PR's template
   *     banner at module-eval time (race-free; set before our async fetch resolves).
   *  2. A `[data-overskill-push-recovery="react"]` element — this PR's marked banner,
   *     if already in the DOM.
   *  3. A #3792-era unmarked React banner: a `role="alert"` element carrying the shared
   *     copy. (Our own banner lives in a Shadow DOM, so its text never leaks into this
   *     light-DOM scan and can't self-match.)
   */
  private reactBannerPresent(): boolean {
    try {
      if ((window as unknown as Record<string, unknown>)[REACT_BANNER_FLAG] === true) {
        return true;
      }
      if (document.querySelector(`[${BANNER_ATTR}="react"]`)) {
        return true;
      }
      const alerts = document.querySelectorAll('[role="alert"]');
      for (let i = 0; i < alerts.length; i++) {
        const text = alerts[i].textContent || '';
        if (text.indexOf('Notifications stopped working') !== -1) return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  private isDismissed(): boolean {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  }

  /**
   * Read the persisted app-user JWT — but ONLY if it hasn't been superseded by a
   * cross-origin logout (issue #2169).
   *
   * overskill-sdk's AuthClient drops any stored token whose `iat` predates the
   * `.overskill.app`-scoped `overskill_logout_<appId>` marker BEFORE it is ever sent
   * (auth-client.ts:38). platform.js, however, runs and reads `overskill_token`
   * BEFORE the app bundle constructs that AuthClient — so without this check a
   * logged-out user's stale sibling-origin JWT would still be replayed on the
   * `/api/push/status` probe AND the fallback subscribe POST (CWE-613). We apply
   * the IDENTICAL supersession check here and, matching AuthClient, evict the
   * stale token from localStorage so it can never be sent by either caller.
   */
  private getToken(): string | null {
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (!token) return null;
      if (isTokenSupersededByLogout(token, readLogoutMarker(token, this.config.appId))) {
        this.debugLog('Stored token superseded by cross-origin logout marker — clearing');
        try {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
        } catch {
          // localStorage may be unavailable (Safari private mode); ignore.
        }
        return null;
      }
      return token;
    } catch {
      return null;
    }
  }

  /**
   * GET the app's OWN `/api/push/status` and read ONLY `health.needs_resubscribe`.
   * Any non-ok status (401 unauthenticated, 5xx relay hiccup), a network error, or a
   * malformed body resolves to `false` — never a banner on a transient failure.
   */
  private async fetchNeedsResubscribe(token: string): Promise<boolean> {
    try {
      const res = await fetch(STATUS_PATH, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return false;
      const data = await res.json().catch(() => null);
      return !!(data && data.health && data.health.needs_resubscribe === true);
    } catch {
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  // Rendering (Shadow DOM — tightly scoped, no global CSS, no scroll lock)
  // ---------------------------------------------------------------------------

  private renderBanner(): void {
    try {
      if (!document.body) return; // nothing to attach to (fail-quiet)
      // Idempotent: never insert a second SDK banner.
      if (document.querySelector(`[${BANNER_ATTR}="sdk"]`)) return;

      const host = document.createElement('div');
      // Shadow DOM is REQUIRED — it is how styles stay scoped (no global CSS) and how the
      // banner text stays invisible to reactBannerPresent()'s light-DOM scan. Any browser
      // that passes the Web Push support gate supports it; if somehow absent, bail rather
      // than leak global styles.
      if (typeof host.attachShadow !== 'function') return;
      host.setAttribute(BANNER_ATTR, 'sdk');
      // The host is a fixed overlay; it never shifts existing layout and never locks
      // scrolling. Positioning stays on the host (light DOM) so the shadow tree only
      // needs to style its own contents.
      host.style.position = 'fixed';
      host.style.top = '0';
      host.style.left = '0';
      host.style.right = '0';
      host.style.zIndex = '2147483000'; // just below the max, above app chrome
      host.style.pointerEvents = 'none'; // let the inner bar re-enable pointer events

      const root: ShadowRoot = host.attachShadow({ mode: 'open' });

      const style = document.createElement('style');
      style.textContent = BANNER_STYLES;
      root.appendChild(style);

      // role="alert" lives INSIDE the shadow tree: still exposed to assistive tech, but
      // invisible to reactBannerPresent()'s light-DOM `[role="alert"]` scan.
      const bar = document.createElement('div');
      bar.setAttribute('role', 'alert');
      bar.className = 'bar';

      const inner = document.createElement('div');
      inner.className = 'inner';

      const icon = document.createElement('span');
      icon.className = 'icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '!'; // vanilla, dependency-free glyph

      const msg = document.createElement('p');
      msg.className = 'msg';
      msg.textContent = BANNER_COPY;

      const enableBtn = document.createElement('button');
      enableBtn.type = 'button';
      enableBtn.className = 'enable';
      enableBtn.textContent = 'Turn on';

      const dismissBtn = document.createElement('button');
      dismissBtn.type = 'button';
      dismissBtn.className = 'dismiss';
      dismissBtn.setAttribute('aria-label', 'Dismiss');
      dismissBtn.textContent = '×'; // ×

      enableBtn.addEventListener('click', () => {
        void this.handleReenable(host, enableBtn);
      });
      dismissBtn.addEventListener('click', () => {
        this.handleDismiss(host);
      });

      inner.appendChild(icon);
      inner.appendChild(msg);
      inner.appendChild(enableBtn);
      inner.appendChild(dismissBtn);
      bar.appendChild(inner);
      root.appendChild(bar);

      document.body.appendChild(host);
      this.debugLog('Banner rendered');
    } catch {
      // Rendering must never break the host app.
    }
  }

  private removeBanner(host: HTMLElement): void {
    try {
      host.parentNode?.removeChild(host);
    } catch {
      /* ignore */
    }
  }

  private handleDismiss(host: HTMLElement): void {
    try {
      // Per-session ONLY. NEVER localStorage — that would permanently silence the surface
      // and recreate the original OS-V7HMWA bug.
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    this.removeBanner(host);
  }

  private async handleReenable(host: HTMLElement, btn: HTMLButtonElement): Promise<void> {
    try {
      btn.disabled = true;
      btn.textContent = 'Enabling…';
      this.lastPermission = null;
      const ok = await this.subscribe();
      if (ok) {
        try {
          // A fresh subscription supersedes the dismissal so a FUTURE breakage shows again.
          sessionStorage.removeItem(DISMISS_KEY);
        } catch {
          /* ignore */
        }
        this.removeBanner(host);
      } else {
        // Failed / denied — leave the banner up so the user can retry, and say why in
        // one line instead of silently resetting the button (SW-250).
        this.showFailureReason(host);
        btn.disabled = false;
        btn.textContent = 'Turn on';
      }
    } catch {
      try {
        btn.disabled = false;
        btn.textContent = 'Turn on';
      } catch {
        /* ignore */
      }
    }
  }

  private showFailureReason(host: HTMLElement): void {
    try {
      const msg = host.shadowRoot?.querySelector('.msg');
      if (!msg) return;
      msg.textContent = pushReenableFailureReason({
        permission: this.lastPermission || currentPermission(),
        isIOS: detectIOS(),
        standalone: detectStandalone(),
      });
    } catch {
      /* ignore */
    }
  }

  // ---------------------------------------------------------------------------
  // Re-subscribe
  // ---------------------------------------------------------------------------

  /**
   * Prefer an app-provided subscribe path if one is exposed on `window.overskill.push`.
   * Otherwise fall back to a self-contained flow that mirrors overskill-sdk's
   * PushClient.subscribe() EXACTLY — same VAPID endpoint, same platform subscribe route,
   * same flat `p256dh_key`/`auth_key` body. (The generated worker deliberately has NO
   * subscribe route; the real one is a Rails platform route. We use the real endpoint
   * rather than invent one.)
   */
  private async subscribe(): Promise<boolean> {
    try {
      const appPush = (window as unknown as {
        overskill?: { push?: { subscribe?: (opts?: unknown) => Promise<boolean> } };
      }).overskill?.push;
      if (appPush && typeof appPush.subscribe === 'function') {
        const result = await appPush.subscribe({
          // Called for ANY non-granted answer, including a dismissed prompt
          // (`default`, retryable) — so record the real state, never assume
          // "blocked in settings".
          onPermissionDenied: () => {
            this.lastPermission = currentPermission();
          },
        });
        return result === true;
      }
      return await this.selfContainedSubscribe();
    } catch {
      return false;
    }
  }

  private async selfContainedSubscribe(): Promise<boolean> {
    try {
      if (!this.isPushSupported()) return false;
      const appId = this.config.appId;
      if (!appId || appId === 'unknown') return false;

      const platformBase = this.resolvePlatformBase();
      // Reuse the push-capable worker that already owns root scope (#3705).
      // Registering SW_PATH would replace it, killing the app's precache and
      // re-opening the scope war that made push silently dead in the first
      // place. Only register our own script when nothing push-capable owns "/".
      // Resolve ONCE. The guarded `register` would re-run the same resolution
      // (and its ping waits) before the permission prompt, so once we know no
      // push-capable worker owns "/", call the saved native register directly.
      const registration =
        (await resolveRootPushRegistration()) ?? (await registerRootPushWorker());
      await navigator.serviceWorker.ready;

      // Permission prompt ONLY here — driven by the user's click, never on load. Kept
      // before the network fetch so Safari's transient-activation window isn't exceeded.
      const permission = await Notification.requestPermission();
      this.lastPermission = permission;
      if (permission !== 'granted') return false;

      const vapidRes = await fetch(`${platformBase}/api/v1/web_push/vapid_public_key`);
      if (!vapidRes.ok) return false;
      const vapid = (await vapidRes.json().catch(() => null)) as { public_key?: string } | null;
      const publicKey = vapid?.public_key;
      if (!publicKey || typeof publicKey !== 'string') return false;

      const applicationServerKey = urlBase64ToUint8Array(publicKey);
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as unknown as BufferSource,
      });

      const json = subscription.toJSON();
      const token = this.getToken();
      await fetch(`${platformBase}/api/v1/apps/${encodeURIComponent(appId)}/push_subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          endpoint: json.endpoint,
          p256dh_key: json.keys?.p256dh,
          auth_key: json.keys?.auth,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
          app_version: undefined,
        }),
      });
      return true;
    } catch {
      return false;
    }
  }

  /** Mirrors overskill-sdk resolvePushPlatformBase(): BRIDGE_URL, else production. */
  private resolvePlatformBase(): string {
    let configured: unknown = this.config.bridgeUrl;
    try {
      if (!configured) configured = window.APP_CONFIG?.BRIDGE_URL;
    } catch {
      /* ignore */
    }
    const base =
      typeof configured === 'string' && /^https?:\/\//i.test(configured)
        ? configured
        : 'https://www.overskill.com';
    return base.replace(/\/+$/, '');
  }

  private debugLog(message: string): void {
    if (this.config.debug) {
      // Only under explicit debug mode; never a user-visible error.
      console.log(`[OverSkill Platform SDK] PushRecovery: ${message}`);
    }
  }

  // Public API
  isEnabled(): boolean {
    return this.enabled;
  }
}

// ---------------------------------------------------------------------------
// Cross-origin logout invalidation (issue #2169)
// ---------------------------------------------------------------------------
// Kept in lockstep with overskill-sdk/src/auth-session.ts. DUPLICATED here (not
// imported) because platform.js is a standalone CDN bundle with NO build-time
// dependency on the app's overskill-sdk. Semantics MUST match auth-session.ts
// EXACTLY: same per-app cookie name, same app-id resolution (token `aud`, then the
// configured appId), same `iat`-in-ms source, same marker-wins-ties (`>=`)
// comparison. See getToken() for why platform.js must run this itself.

/** Read `token`'s app's logout marker timestamp (ms) from `document.cookie`, or null. Mirrors AuthClient.readLogoutMarker(). */
function readLogoutMarker(token: string | null, configuredAppId: string | null | undefined): number | null {
  if (typeof document === 'undefined') return null;
  try {
    return parseLogoutMarker(document.cookie, logoutMarkerAppId(token, configuredAppId));
  } catch {
    return null;
  }
}

/** The per-app marker cookie name, or null for an id that cannot name a cookie. Mirrors auth-session.logoutCookieName. */
function logoutCookieName(appId: string | null | undefined): string | null {
  if (typeof appId !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(appId)) return null;
  return LOGOUT_COOKIE_NAME + '_' + appId;
}

/** The app a token was minted for, from its `overskill-app-<id>` audience. Mirrors auth-session.tokenAppId. */
function tokenAppId(token: string | null | undefined): string | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(decodeBase64Url(parts[1]));
    const audiences = Array.isArray(payload.aud) ? payload.aud : [payload.aud];
    for (const aud of audiences) {
      if (typeof aud !== 'string' || aud.indexOf('overskill-app-') !== 0) continue;
      const appId = aud.slice('overskill-app-'.length);
      if (logoutCookieName(appId)) return appId;
    }
    return null;
  } catch {
    return null;
  }
}

/** Which app's marker governs `token`: its own app, else the configured appId. Mirrors auth-session.logoutMarkerAppId. */
function logoutMarkerAppId(
  token: string | null | undefined,
  configuredAppId: string | null | undefined
): string | null {
  const fromToken = tokenAppId(token);
  if (fromToken) return fromToken;
  return logoutCookieName(configuredAppId) ? (configuredAppId as string) : null;
}

/** Parse `appId`'s logout-marker timestamp (ms since epoch) from a cookie string. Mirrors auth-session.parseLogoutMarker. */
function parseLogoutMarker(
  cookieString: string | undefined | null,
  appId: string | null | undefined
): number | null {
  const cookieName = logoutCookieName(appId);
  if (!cookieName || !cookieString) return null;
  for (const part of cookieString.split(';')) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    const name = part.slice(0, eq).trim();
    if (name !== cookieName) continue;
    const value = part.slice(eq + 1).trim();
    const ms = parseInt(value, 10);
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}

/** base64url → string. JWT segments are base64url and may be unpadded. Mirrors auth-session.decodeBase64Url. */
function decodeBase64Url(segment: string): string {
  let b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4 !== 0) b64 += '=';
  return atob(b64);
}

/**
 * Decode a JWT's `iat` (issued-at) claim and return it in MILLISECONDS. No signature
 * verification — this value is ONLY compared against the logout marker, never used to
 * grant access. Returns null when the token is malformed or carries no numeric iat.
 * Mirrors auth-session.tokenIssuedAtMs.
 */
function tokenIssuedAtMs(token: string | null | undefined): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(decodeBase64Url(parts[1]));
    if (typeof payload.iat === 'number' && Number.isFinite(payload.iat)) {
      return payload.iat * 1000;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Decide whether a stored token has been superseded by a logout (possibly on a sibling
 * origin). Returns true (stale — clear it) when a logout marker exists AND it is at
 * least as recent as the token's iat. An unparseable iat with a present marker is
 * treated as stale (force re-login rather than honor a token after a logout). The
 * marker wins ties (`>=`). Mirrors auth-session.isTokenSupersededByLogout EXACTLY.
 */
function isTokenSupersededByLogout(
  token: string | null | undefined,
  logoutMarkerMs: number | null
): boolean {
  if (logoutMarkerMs == null) return false; // no logout recorded → honor token
  if (!token) return false; // nothing to invalidate
  const iatMs = tokenIssuedAtMs(token);
  if (iatMs == null) return true; // unparseable token + a logout marker → stale
  return logoutMarkerMs >= iatMs;
}

/**
 * VAPID public keys are URL-safe base64. Convert to Uint8Array for PushManager.subscribe().
 * Lifted verbatim from overskill-sdk's push-client (the standard MDN/web.dev recipe) so
 * the fallback produces byte-identical subscriptions.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Scoped styles for the Shadow DOM banner. Amber, matching the template banner, with a
 * dark-mode variant. Everything is contained inside the shadow tree — no global CSS.
 */
const BANNER_STYLES = `
:host { all: initial; }
.bar {
  pointer-events: auto;
  box-sizing: border-box;
  width: 100%;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  background: #f59e0b;
  color: #451a03;
  border-bottom: 1px solid rgba(180, 83, 9, 0.4);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}
.inner {
  box-sizing: border-box;
  max-width: 48rem;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
}
.icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 9999px;
  border: 2px solid currentColor;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1;
}
.msg {
  flex: 1 1 auto;
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
}
.enable {
  flex: 0 0 auto;
  cursor: pointer;
  border: 0;
  border-radius: 0.375rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  background: #451a03;
  color: #fffbeb;
  transition: background 0.15s ease, opacity 0.15s ease;
}
.enable:hover { background: #78350f; }
.enable:disabled { opacity: 0.6; cursor: default; }
.dismiss {
  flex: 0 0 auto;
  cursor: pointer;
  border: 0;
  background: transparent;
  color: rgba(69, 26, 3, 0.7);
  font-size: 1.1rem;
  line-height: 1;
  padding: 0.25rem 0.4rem;
  border-radius: 0.375rem;
}
.dismiss:hover { color: #451a03; }
@media (prefers-color-scheme: dark) {
  .bar { background: #451a03; color: #fffbeb; }
  .enable { background: #fffbeb; color: #451a03; }
  .enable:hover { background: #fef3c7; }
  .dismiss { color: rgba(255, 251, 235, 0.7); }
  .dismiss:hover { color: #fffbeb; }
}
`;
